import React, { useCallback, useEffect, useMemo } from 'react';
import { useAPIWrapper, DetailBySchema, DetailFieldLayout } from 'drf-react-by-schema';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';

import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import HomeIcon from '@mui/icons-material/Home';

import { detailAccordion, detailDadosBasicos, detailNonAccordion } from 'src/styles/layout';
import { Layout } from 'src/styles';

import { useAppSelector } from 'src/app/hooks';
import { setPage } from 'src/features/pageSlice';
import { appDispatch, dateFormatted } from 'src/utils/utils';
import { FlocosProductionStatus, getFlocosProductionLabel } from 'src/constants';

import AsmubipButton from 'src/components/AsmubipButton';
import { PageType } from 'src/constants/PageType';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';
import { CAN_REVIEW_FLOCOS } from 'src/constants/Permission';

const MODEL = 'flocos_production';

function FlocosProductionDetail() {
  const { id, formValues, perfil, isMobile } = useAppSelector((state) => state.page);
  const { pageForm, loadSinglePageData } = useAPIWrapper();
  const navigate = useNavigate();
  const [canReviewFlocos] = useHasPermissionsMulti([CAN_REVIEW_FLOCOS]);
  const [URLSearchParams] = useSearchParams();
  const savedType = URLSearchParams.get('type');

  const key = MODEL;

  const handleEditClick = useCallback(() => {
    navigate(`/${key}/edit/${id}`);
  }, [key, id]);

  const handleReviewClick = useCallback(() => {
    navigate(`/flocos_review/edit/${id}`);
  }, [id]);

  const handleInicioClick = useCallback(() => {
    navigate('/');
  }, []);

  const status = useMemo(() => {
    return formValues?.status?.value as FlocosProductionStatus | undefined;
  }, [formValues?.status]);

  const perfilEntreposto = useMemo(() => perfil?.entreposto, [perfil]);

  const statusName = useMemo(() => {
    if (!status) {
      return;
    }
    return getFlocosProductionLabel({ status });
  }, [status]);

  const statusHelper = useMemo(() => {
    if (!status) {
      return;
    }
    return getFlocosProductionLabel({ status, helperText: true });
  }, [status]);

  const fieldsLayout = useMemo(() => {
    if (!status) {
      return;
    }

    const newFieldsLayout: DetailFieldLayout[] = [
      {
        title: 'Dados do registro',
        rows: ['lote', 'data_registro', 'perfil', 'ajudantes', 'grupo'],
        isAccordion: true,
        ...detailDadosBasicos,
      },
    ];

    if (
      [
        FlocosProductionStatus.READY_TO_REVIEW,
        FlocosProductionStatus.REVIEWED,
        FlocosProductionStatus.COMPLETE,
      ].includes(status)
    ) {
      const pesagemSecoFieldsLayout: DetailFieldLayout = {
        title: 'Produção',
        rows: ['peso_flocos_seco', 'data_flocos_seco'],
        isAccordion: false,
        ...detailNonAccordion,
      };
      newFieldsLayout.push(pesagemSecoFieldsLayout);
    }

    const isAccordion = ![
      FlocosProductionStatus.IN_PRODUCTION,
      FlocosProductionStatus.REVIEWED,
    ].includes(status);

    const pesagemUmidoFieldsLayout: DetailFieldLayout = {
      title: 'Floco úmido',
      rows: ['data_flocos_umido', 'peso_flocos_umido'],
      isAccordion,
      isAccordionOpen: !isAccordion,
      ...(isAccordion ? detailAccordion : detailNonAccordion),
    };
    newFieldsLayout.push(pesagemUmidoFieldsLayout);

    const coletaFieldsLayout: DetailFieldLayout = {
      title: 'Coleta do Coco',
      rows: ['data_coleta'],
      isAccordion,
      isAccordionOpen: !isAccordion,
      ...(isAccordion ? detailAccordion : detailNonAccordion),
    };
    newFieldsLayout.push(coletaFieldsLayout);

    if ([FlocosProductionStatus.REVIEWED, FlocosProductionStatus.COMPLETE].includes(status)) {
      newFieldsLayout.push({
        CustomElement: (
          <Box
            sx={
              isMobile
                ? undefined
                : {
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }
            }>
            <AsmubipButton
              variant="contained-secondary"
              aria-label="editar"
              size="large"
              fullWidth={isMobile}
              onClick={handleEditClick}
              startIcon={<EditIcon />}>
              Editar
            </AsmubipButton>
          </Box>
        ),
      });
      const reviewedFieldsLayout: DetailFieldLayout = {
        title: 'Revisão do Lote',
        rows: [
          ...(perfilEntreposto ? [] : ['entreposto']),
          'data_revisao',
          'peso_flocos_revisao',
          'responsavel_grupo_revisao',
          'responsavel_entreposto_revisao',
          'observacoes_revisao',
          'comentarios_revisao',
          'foto_revisao',
        ],
        isAccordion: false,
        ...detailNonAccordion,
      };
      newFieldsLayout.push(reviewedFieldsLayout);
    }

    return newFieldsLayout;
  }, [status, perfilEntreposto, isMobile, handleEditClick]);

  useEffect(() => {
    if (!perfil || !id || !key) {
      return;
    }
    const loadAllDataAsync = async () => {
      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
      });
      if (values) {
        const pageTitle = !savedType
          ? 'Produção de flocos de mesocarpo'
          : savedType === PageType.CREATE
            ? 'Produção concluída!'
            : 'Produção editada!';
        appDispatch(
          setPage({
            id,
            model: MODEL,
            formValues: values,
            title: pageTitle,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, key, savedType, perfil]);

  if (
    !perfil ||
    !pageForm?.schema ||
    !pageForm.columns ||
    !formValues ||
    !id ||
    !fieldsLayout ||
    !status ||
    !statusName ||
    !statusHelper
  ) {
    return null;
  }

  return (
    <>
      {savedType ? (
        <Card>
          <CardContent sx={Layout.productionDetailCardContent}>
            <Box sx={Layout.productionDetailCardIconWrapper}>
              <CheckIcon sx={Layout.productionCardIcon} />
            </Box>
            <Typography variant="h6">
              Produção de flocos {dateFormatted(formValues.data_flocos_seco)}
            </Typography>
            <Typography variant="subtitle1">{statusHelper}</Typography>
          </CardContent>
          <CardActions>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              sx={Layout.fullWidthButton}
              onClick={handleInicioClick}>
              Início
            </Button>
          </CardActions>
        </Card>
      ) : (
        <Container>
          <Card sx={Layout.BottomInfoContainerCard}>
            <CardContent>
              <Typography variant="subtitle1">{statusName}</Typography>
              <Typography variant="body2">{statusHelper}</Typography>
            </CardContent>
          </Card>
        </Container>
      )}
      <DetailBySchema
        schema={pageForm.schema}
        columns={pageForm.columns}
        values={formValues}
        fieldsLayout={fieldsLayout}
        elevation={0}
      />
      <Box
        sx={
          isMobile
            ? undefined
            : {
                display: 'flex',
                justifyContent: 'flex-end',
                paddingBottom: 4,
                marginTop: 2,
                gap: 2,
              }
        }>
        {[FlocosProductionStatus.IN_PRODUCTION, FlocosProductionStatus.READY_TO_REVIEW].includes(
          status
        ) && (
          <AsmubipButton
            variant="contained-secondary"
            aria-label="editar"
            size="large"
            fullWidth={isMobile}
            onClick={handleEditClick}
            startIcon={<EditIcon />}>
            Editar
          </AsmubipButton>
        )}
        {[
          FlocosProductionStatus.READY_TO_REVIEW,
          FlocosProductionStatus.REVIEWED,
          FlocosProductionStatus.COMPLETE,
        ].includes(status) &&
          canReviewFlocos && (
            <AsmubipButton
              variant="contained-dark"
              aria-label="revisar"
              size="large"
              fullWidth={isMobile}
              onClick={handleReviewClick}
              sx={isMobile ? { mt: 4 } : undefined}>
              {status === FlocosProductionStatus.READY_TO_REVIEW ? 'Revisar' : 'Corrigir revisão'}
            </AsmubipButton>
          )}
      </Box>
      {/* isMobile && (
        <Container sx={Layout.BottomInfoContainer}>
          <Card sx={Layout.BottomInfoContainerCard}>
            <CardContent>
              <Typography variant="subtitle1">{statusName}</Typography>
              <Typography variant="body2">{statusHelper}</Typography>
            </CardContent>
          </Card>
        </Container>
      ) */}
    </>
  );
}

export default React.memo(FlocosProductionDetail);
